import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { CouponService } from '../../core/services/coupon.service';
import { Coupon } from '../../models/coupon.interface';

@Component({
  selector: 'app-coupons',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Promo Coupons"
      pageSubtitle="Manage discount coupons, redemption rules & checkout promos"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- HEADER & ACTIONS -->
        <div class="page-header glass-card">
          <div class="page-header-left">
            <div class="icon-box icon-box-gold icon-box-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="19" y1="5" x2="5" y2="19"></line>
                <circle cx="6.5" cy="6.5" r="2.5"></circle>
                <circle cx="17.5" cy="17.5" r="2.5"></circle>
              </svg>
            </div>
            <div>
              <h2 class="font-serif title-gold" style="font-size:16px;">Coupons &amp; Promo Codes</h2>
              <p class="sub-text">{{ coupons.length }} active &amp; scheduled promo codes</p>
            </div>
          </div>
          <button class="btn-gold" (click)="openAddModal()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Create New Coupon
          </button>
        </div>

        <!-- STATS ROW -->
        <div class="stats-row">
          <div class="stat-card glass-card">
            <span class="stat-icon">🏷️</span>
            <div>
              <div class="stat-value">{{ coupons.length }}</div>
              <div class="stat-label">Total Coupons</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <span class="stat-icon">✨</span>
            <div>
              <div class="stat-value">{{ getActiveCount() }}</div>
              <div class="stat-label">Active &amp; Redeemable</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <span class="stat-icon">⚡</span>
            <div>
              <div class="stat-value">{{ getTotalUsageCount() }}</div>
              <div class="stat-label">Total Redemptions</div>
            </div>
          </div>
        </div>

        <!-- SEARCH BAR -->
        <div class="filter-bar glass-card">
          <div class="search-field">
            <input
              type="text"
              class="form-control"
              placeholder="Search coupon code or description..."
              [(ngModel)]="searchQuery"
            />
          </div>
        </div>

        <!-- COUPONS TABLE -->
        <div class="table-card glass-card">
          <div class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th>CODE</th>
                  <th>DISCOUNT</th>
                  <th>MIN ORDER</th>
                  <th>EXPIRY</th>
                  <th>REDEMPTIONS</th>
                  <th>STATUS</th>
                  <th style="text-align: right;">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let c of filteredCoupons">
                  <td>
                    <div class="code-badge-wrap">
                      <span class="code-badge">{{ c.code }}</span>
                      <button class="btn-copy" (click)="copyCode(c.code)" title="Copy Code">📋</button>
                    </div>
                    <small *ngIf="c.description" class="sub-text" style="display:block;margin-top:4px;">
                      {{ c.description }}
                    </small>
                  </td>
                  <td>
                    <strong style="color: var(--accent-gold);">
                      {{ c.discountType === 'percentage' ? c.discountValue + '%' : '₹' + c.discountValue }}
                    </strong>
                    <span *ngIf="c.discountType === 'percentage' && c.maxDiscountAmount" class="sub-text" style="display:block;font-size:10.5px;">
                      (Cap: ₹{{ c.maxDiscountAmount }})
                    </span>
                  </td>
                  <td>
                    {{ c.minOrderAmount ? '₹' + c.minOrderAmount : 'No Minimum' }}
                  </td>
                  <td>
                    <span *ngIf="c.expiresAt" [class.text-danger]="isExpired(c.expiresAt)">
                      {{ c.expiresAt | date:'mediumDate' }}
                      <small *ngIf="isExpired(c.expiresAt)" style="color:#ef4444;display:block;font-size:10px;">(Expired)</small>
                    </span>
                    <span *ngIf="!c.expiresAt" class="sub-text">Never Expires</span>
                  </td>
                  <td>
                    <span>{{ c.usedCount || 0 }}</span>
                    <span class="sub-text"> / {{ c.usageLimit ? c.usageLimit : '∞' }}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="status-toggle-btn"
                      [class.active]="c.active && !isExpired(c.expiresAt)"
                      (click)="toggleActive(c)"
                      title="Click to toggle active state"
                    >
                      {{ c.active && !isExpired(c.expiresAt) ? 'Active' : 'Inactive' }}
                    </button>
                  </td>
                  <td style="text-align: right;">
                    <div style="display: flex; gap: 8px; justify-content: flex-end;">
                      <button class="action-btn action-btn-gold" (click)="editCoupon(c)" title="Edit Coupon">
                        ✏️
                      </button>
                      <button class="action-btn action-btn-danger" (click)="deleteCoupon(c)" title="Delete Coupon">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>

                <tr *ngIf="filteredCoupons.length === 0">
                  <td colspan="7" style="text-align: center; padding: 40px; color: var(--text-muted);">
                    No coupons found. Click "Create New Coupon" to add one!
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>

    <!-- CREATE / EDIT MODAL -->
    <div class="modal-overlay" *ngIf="showModal" (click)="closeModal()">
      <div class="modal-card glass-card" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3 class="font-serif title-gold">{{ isEditMode ? 'Edit Promo Coupon' : 'Create New Promo Coupon' }}</h3>
          <button class="close-btn" (click)="closeModal()">&times;</button>
        </div>

        <form (ngSubmit)="saveCoupon()" class="modal-form">
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">Coupon Code *</label>
              <input
                type="text"
                class="form-control code-input"
                [(ngModel)]="formData.code"
                name="code"
                placeholder="e.g. SHELL20 or VIP100"
                required
                style="text-transform: uppercase; font-family: monospace; font-weight: 700; letter-spacing: 0.1em;"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Discount Type *</label>
              <select class="form-control" [(ngModel)]="formData.discountType" name="discountType">
                <option value="percentage">Percentage (%) Off</option>
                <option value="fixed">Fixed Amount (₹) Off</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">
                {{ formData.discountType === 'percentage' ? 'Discount Percentage (%) *' : 'Discount Amount (₹) *' }}
              </label>
              <input
                type="number"
                class="form-control"
                [(ngModel)]="formData.discountValue"
                name="discountValue"
                placeholder="e.g. 20"
                min="0"
                required
              />
            </div>

            <div class="form-group" *ngIf="formData.discountType === 'percentage'">
              <label class="form-label">Maximum Discount Cap (₹)</label>
              <input
                type="number"
                class="form-control"
                [(ngModel)]="formData.maxDiscountAmount"
                name="maxDiscountAmount"
                placeholder="e.g. 500 (0 for no cap)"
                min="0"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Minimum Order Amount (₹)</label>
              <input
                type="number"
                class="form-control"
                [(ngModel)]="formData.minOrderAmount"
                name="minOrderAmount"
                placeholder="e.g. 999 (0 for no min)"
                min="0"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Expiry Date (Optional)</label>
              <input
                type="date"
                class="form-control"
                [(ngModel)]="formData.expiresAt"
                name="expiresAt"
              />
            </div>

            <div class="form-group">
              <label class="form-label">Usage Limit (0 for unlimited)</label>
              <input
                type="number"
                class="form-control"
                [(ngModel)]="formData.usageLimit"
                name="usageLimit"
                placeholder="0"
                min="0"
              />
            </div>

            <div class="form-group" style="grid-column: span 2;">
              <label class="form-label">Internal Description / Campaign Notes</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="formData.description"
                name="description"
                placeholder="e.g. 20% festive discount for Instagram followers"
              />
            </div>

            <div class="form-group" style="grid-column: span 2;">
              <label class="checkbox-label">
                <input type="checkbox" [(ngModel)]="formData.active" name="active" />
                <span>Active &amp; Ready for Checkout Redemption</span>
              </label>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-secondary" (click)="closeModal()">Cancel</button>
            <button type="submit" class="btn-gold" [disabled]="saving">
              {{ saving ? 'Saving...' : (isEditMode ? 'Update Coupon' : 'Create Coupon') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      margin-bottom: 24px;
    }
    .page-header-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .stats-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 24px;
    }
    .stat-card {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 18px 24px;
    }
    .stat-icon { font-size: 24px; }
    .stat-value { font-size: 22px; font-weight: 700; color: var(--accent-gold); }
    .stat-label { font-size: 12px; color: var(--text-muted); }

    .filter-bar {
      padding: 12px 18px;
      margin-bottom: 20px;
    }
    .code-badge-wrap {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .code-badge {
      font-family: monospace;
      font-weight: 700;
      letter-spacing: 0.08em;
      background: rgba(201, 169, 110, 0.12);
      color: var(--accent-gold);
      border: 1px solid rgba(201, 169, 110, 0.3);
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 13px;
    }
    .btn-copy {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 12px;
      opacity: 0.7;
    }
    .btn-copy:hover { opacity: 1; }

    .status-toggle-btn {
      padding: 4px 10px;
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }
    .status-toggle-btn.active {
      background: rgba(34, 197, 94, 0.15);
      color: #22c55e;
      border-color: rgba(34, 197, 94, 0.3);
    }

    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.7);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
    }
    .modal-card {
      width: 100%;
      max-width: 600px;
      padding: 28px;
      border: 1px solid var(--border-color);
    }
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--border-color);
    }
    .close-btn {
      background: none;
      border: none;
      color: var(--text-muted);
      font-size: 24px;
      cursor: pointer;
    }
    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid var(--border-color);
    }
    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      cursor: pointer;
    }
  `]
})
export class CouponsComponent implements OnInit {
  coupons: Coupon[] = [];
  searchQuery = '';
  showModal = false;
  isEditMode = false;
  saving = false;
  editingId: string | null = null;

  formData: Partial<Coupon> = {
    code: '',
    discountType: 'percentage',
    discountValue: 10,
    minOrderAmount: 0,
    maxDiscountAmount: 0,
    expiresAt: null,
    usageLimit: 0,
    active: true,
    description: ''
  };

  constructor(private couponService: CouponService) {}

  ngOnInit(): void {
    this.loadCoupons();
  }

  loadCoupons(): void {
    this.couponService.getCoupons().subscribe({
      next: (res) => (this.coupons = res || []),
      error: (err) => console.error('Failed to load coupons:', err)
    });
  }

  get filteredCoupons(): Coupon[] {
    if (!this.searchQuery.trim()) return this.coupons;
    const q = this.searchQuery.toLowerCase().trim();
    return this.coupons.filter(
      (c) => c.code.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)
    );
  }

  getActiveCount(): number {
    return this.coupons.filter((c) => c.active && !this.isExpired(c.expiresAt)).length;
  }

  getTotalUsageCount(): number {
    return this.coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0);
  }

  isExpired(dateStr?: string | null): boolean {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  }

  openAddModal(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.formData = {
      code: '',
      discountType: 'percentage',
      discountValue: 10,
      minOrderAmount: 0,
      maxDiscountAmount: 0,
      expiresAt: null,
      usageLimit: 0,
      active: true,
      description: ''
    };
    this.showModal = true;
  }

  editCoupon(c: Coupon): void {
    this.isEditMode = true;
    this.editingId = c._id || null;
    this.formData = {
      code: c.code,
      discountType: c.discountType,
      discountValue: c.discountValue,
      minOrderAmount: c.minOrderAmount || 0,
      maxDiscountAmount: c.maxDiscountAmount || 0,
      expiresAt: c.expiresAt ? new Date(c.expiresAt).toISOString().split('T')[0] : null,
      usageLimit: c.usageLimit || 0,
      active: c.active !== false,
      description: c.description || ''
    };
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  copyCode(code: string): void {
    navigator.clipboard?.writeText(code);
    alert(`Coupon code "${code}" copied to clipboard!`);
  }

  toggleActive(c: Coupon): void {
    if (!c._id) return;
    this.couponService.updateCoupon(c._id, { active: !c.active }).subscribe({
      next: (updated) => {
        c.active = updated.active;
      },
      error: (err) => alert('Failed to toggle coupon status: ' + err.message)
    });
  }

  deleteCoupon(c: Coupon): void {
    if (!c._id) return;
    if (!confirm(`Are you sure you want to delete coupon code "${c.code}"?`)) return;
    this.couponService.deleteCoupon(c._id).subscribe({
      next: () => {
        this.coupons = this.coupons.filter((it) => it._id !== c._id);
      },
      error: (err) => alert('Failed to delete coupon: ' + err.message)
    });
  }

  saveCoupon(): void {
    if (!this.formData.code || this.formData.discountValue === undefined) {
      alert('Please fill out code and discount value');
      return;
    }
    this.saving = true;

    if (this.isEditMode && this.editingId) {
      this.couponService.updateCoupon(this.editingId, this.formData).subscribe({
        next: (saved) => {
          this.saving = false;
          this.showModal = false;
          this.loadCoupons();
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to update coupon: ' + (err.error?.message || err.message));
        }
      });
    } else {
      this.couponService.createCoupon(this.formData).subscribe({
        next: (saved) => {
          this.saving = false;
          this.showModal = false;
          this.loadCoupons();
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to create coupon: ' + (err.error?.message || err.message));
        }
      });
    }
  }
}
